import { Signal } from '../signal/Signal';
import { CancelToken, CancelSource, CancelError } from '../cancel/CancelError';
import { TimeoutError } from './TimeoutError';

/**
 * Creates a promise which waits for the given timeout to elapse.
 * @public
 */
export async function wait(time: number, cancelToken?: CancelToken): Promise<void> {
    const {
        resolve,
        reject,
        promise
    } = new Signal<void>();

    if (cancelToken) {
        cancelToken.throwIfCanceled();
    }

    const timeoutId = window.setTimeout(() => resolve(), time);

    const subscription = cancelToken && cancelToken.subscribe(() => {
        window.clearTimeout(timeoutId);

        reject(new CancelError());
    });

    try {
        await promise;
    } finally {
        if (subscription) {
            subscription();
        }
    }
}

/**
 * Creates a cancel token source which will be automatically canceled on a timeout, unless first disposed.
 * Alternatively, if a parent cancel token is provided, the source and timeout will be canceled if the parent
 * is canceled.
 * @public
 */
export function createTimeoutCancelSource(timeout: number, cancelToken?: CancelToken): CancelSource {
    const cancelSource = new CancelSource();

    const subscription = cancelToken && cancelToken.subscribe(cancelSource.cancel);

    const timeoutId = window.setTimeout(() => {
        cancelSource.cancel(new TimeoutError({ timeout }));
    }, timeout);

    cancelSource.token.subscribe(() => {
        window.clearTimeout(timeoutId);
    });

    return {
        ...cancelSource,
        dispose: () => {
            clearTimeout(timeoutId);

            if (subscription) {
                subscription();
            }

            cancelSource.dispose();
        }
    };
}
